import { AnalysisRun, AnalysisRunService } from "./services/analysis-run.service";
import { ANALYSIS_TIMEOUTS, AnalysisError, bounded } from "./utils/analysis-async";
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import { LogService } from "./services/log.service";
import { RiskAssessment } from "./models/risk-assessment";
import { debounceTime, tap, Subject, Subscription } from "rxjs";
import { RiskAssessmentService } from "./services/risk-assessment.service";
import { WaitService } from "./services/wait.service";
import { MessageService } from "./services/message.service";
import { RiskLevel } from "./models/risk-level";
import {
  CacheNamesEnum,
  Constants,
  EvaluationFlowsEnum,
  EvFlow,
  HttpErrorsEnum,
  IEvaluationStepsConfig,
  LANGUAGES,
  TabIndexEnum,
  UserDTO
} from "./models/shared.model";
import { SharedService } from "./services/shared.service";
import { ConfigService } from "./services/config.service";
import { environment } from "src/environments/environment";
import { HOST_NAMES } from "./models/constants";
import { IEvaluator } from "./interfaces/evaluator-interface";
import { EvaluatorService } from "./services/evaluator.service";
import { ScrollToSectionService } from "./services/scrollToSection.service";
import { TranslateService } from "@ngx-translate/core";
import { SettingsEnum } from "./models/settings.model";
import { SettingsService } from "./services/settings.service";
import { LinksService } from "./services/links.service";


@Component({
  selector: "app-home",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"]
})
export default class AppComponent implements OnInit, OnDestroy {
  protected readonly HttpErrorsEnum = HttpErrorsEnum;
  supportedLanguages = ['en', 'fr'];
  user: UserDTO;
  protected readonly languages = LANGUAGES;
  isWebHost: boolean = false;
  isRunning: boolean = true;
  isAnalyzing: boolean = true;
  assessment: RiskAssessment = null as any;
  menuOpen = false;
  tabTitles: string[] = [
    'overview.overview',
    'sender.sender',
    'links.links',
    'attachments.attachments',
    'trusted_domains.trusted',
    'content.content',
    'logs.logs',
    'terms_and_conditions.terms_and_conditions_tab'
  ];
  currentLang = this.translate.currentLang || 'en';
  readonly TabIndexEnum = TabIndexEnum;

  isRetrievingAge = false;

  showError: boolean = false;
  errorMessage: HttpErrorsEnum;

  private subscription: Subscription = new Subscription();
  protected readonly EvaluationFlowsEnum = EvaluationFlowsEnum;
  private evaluationFlowConditions: EvFlow;
  private evaluationFlow: IEvaluationStepsConfig[];
  public env = environment;

  selectedTabIndex = TabIndexEnum.OVERVIEW;

  private currentItemId: string | null = null;
  private destroyed = false;

  private readonly _destroying$ = new Subject<void>();
  currentFlagUrl: string = '';
  constructor(
    public runs: AnalysisRunService,
    private zone: NgZone,
    protected logService: LogService,
    protected messageService: MessageService,
    protected riskService: RiskAssessmentService,
    protected waitService: WaitService,
    protected evaluatorService: EvaluatorService,
    private sharedService: SharedService,
    private configService: ConfigService,
    private scrollToSectionService: ScrollToSectionService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private settingService: SettingsService,
    private linksService: LinksService,
  ) {
    this.translate.addLangs(this.supportedLanguages);
    this.translate.setDefaultLang("en"); // for fallback

    this.currentLang = localStorage.getItem(CacheNamesEnum.LANGUAGE) || 'en';
    this.translate.use(this.currentLang);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscription.unsubscribe();
    this._destroying$.next(undefined);
    this._destroying$.complete();
    this.runs.cancel();
    Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
  }

  async ngOnInit(): Promise<void> {
    Office.onReady(() => { if (!this.destroyed) void this.start(); });
  }

  private async start(): Promise<void> {
    this.subscribeToEvents();
    try {
      await this.sharedService.getToken();
      if (this.destroyed) return;
      this.initializeLanguageSettings();
      this.subscription.add(this.sharedService.itemChanged.pipe(
        tap(forceReload => {
          if (forceReload || this.currentItemId !== Office.context.mailbox.item?.itemId) this.runs.cancel();
        }),
        debounceTime(Constants.MAIL_CHANGE_DEBOUNCE)
      ).subscribe(forceReload => {
        if (this.currentItemId === Office.context.mailbox.item?.itemId && !forceReload &&
            (this.runs.current?.active || this.runs.current?.finished)) return;
        void this.mainLogic();
      }));
      await this.mainLogic();
    } catch (error) {
      if (this.destroyed) return;
      this.showError = true;
      if (Object.values(HttpErrorsEnum).includes(error?.error)) this.errorMessage = error.error;
      else this.handleError(error);
      this.isAnalyzing = false;
      this.waitService.isAnalyzingObserverToggle(false);
      this.waitService.hideMessage();
    }
  }

  subscribeToEvents() {
    this.subscription.add(
      this.waitService.waitingChanged.subscribe((waiting) => {
        this.isRunning = waiting;
        this.cdr.detectChanges();
      })
    );

    Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, () => {
      if (this.destroyed) return;
      Office.context.mailbox.addHandlerAsync(
        Office.EventType.ItemChanged,
        () => {
          this.sharedService.itemChanged.next(false);
        }
      );
    });

    this.subscription.add(
      this.scrollToSectionService.scrollToSection$.subscribe((scrollTo) => {
        this.selectedTabIndex = scrollTo.tab as unknown as TabIndexEnum;
      })
    );

  }

  async mainLogic(): Promise<void> {
    if (this.destroyed) return;
    const item = Office.context.mailbox.item as Office.MessageRead;
    const run = this.runs.begin(item);
    this.currentItemId = item?.itemId ?? null;
    this.evaluationFlow = undefined;
    this.assessment = null as any;
    this.isRetrievingAge = false;
    this.showError = false;
    this.selectedTabIndex = TabIndexEnum.OVERVIEW;
    this.isAnalyzing = true;
    this.waitService.showMessage('Analyzing');
    this.waitService.isAnalyzingObserverToggle(true);
    this.waitService.triggerResetData(true);
    try {
      if (!item) throw new AnalysisError('unavailable', 'No message selected');
      await bounded(() => this.executeRun(run), ANALYSIS_TIMEOUTS.run, 'Analysis', run);
    } catch (error) {
      if (!run.active) return;
      if (!this.evaluationFlow) {
        this.showError = true;
        this.handleError(error);
      }
      run.flows.forEach((status, flow) => {
        if (status.state === 'pending' || status.state === 'running') {
          run.issue(flow, error, 'analysis');
          run.complete(flow);
        }
      });
      this.logService.logException(error instanceof Error ? error : new Error(String(error)));
    } finally {
      if (this.runs.current === run && !run.closed) {
        run.flows.forEach((status, flow) => {
          if (status.state === 'pending' || status.state === 'running') {
            run.issue(flow, new AnalysisError('unavailable', 'Check did not finish'), 'unfinished');
            run.complete(flow);
          }
        });
        try {
          if (this.assessment) this.logService.addChosenItemLog(this.riskService.assessment.getAnalysisResults());
        } catch (error) { console.error('Failed to finalize analysis log', error); }
        run.finish();
        this.zone.run(() => {
          this.isAnalyzing = false;
          this.waitService.isAnalyzingObserverToggle(false);
          this.waitService.hideMessage();
          this.cdr.markForCheck();
        });
      }
    }
  }

  private async executeRun(run: AnalysisRun): Promise<void> {
    await this.fetchData(run);
    run.assertActive();
    this.evaluationFlowConditions = this.sharedService.getFlowPointsAndConditions();
    const configuration = this.evaluationFlowConditions ?? this.configService.getConfiguration();
    this.evaluationFlow = configuration.EvaluationFlows;
    this.evaluatorService.multipleConditions = configuration.multipleConditions;
    run.configure(this.evaluationFlow.map(flow => flow.name));
    this.logService.setUserId(Office.context.mailbox.userProfile.emailAddress);
    this.isWebHost = Office.context.mailbox.diagnostics.hostName === HOST_NAMES.OUTLOOK_WEB_APP;
    const preparation = await Promise.allSettled([
      this.messageService.initialize(run), this.linksService.getEmailBody(run)
    ]);
    run.assertActive();
    if (preparation[0].status === 'rejected') {
      for (const flow of [EvaluationFlowsEnum.SENDER, EvaluationFlowsEnum.IMPERSONATION_RISK])
        run.issue(flow, preparation[0].reason, 'headers');
    }
    if (preparation[1].status === 'rejected') {
      run.issue(EvaluationFlowsEnum.LINKS, preparation[1].reason, 'body');
    }
    await this.runAnalysis(run);
  }

  async fetchData(run: AnalysisRun): Promise<void> {
    await this.sharedService.initUpdateCacheTask();
    run.assertActive();
    this.waitService.dataLoadedToggle(true);
  }

  retryAnalysis(): void { void this.mainLogic(); }

  get incompleteFlows(): string[] {
    return [...(this.runs.current?.flows.entries() || [])]
      .filter(([, status]) => status.state === 'incomplete').map(([flow]) => flow);
  }

  handleError(e: any) {
    if (e?.error?.error) {
      switch (e.error.error) {
        case HttpErrorsEnum.UNKNOWN_CUSTOMER:
          this.errorMessage = HttpErrorsEnum.UNKNOWN_CUSTOMER;
          break;
        case HttpErrorsEnum.AUTHENTICATION_REQUIRED:
          this.errorMessage = HttpErrorsEnum.AUTHENTICATION_REQUIRED;
          break;
        case HttpErrorsEnum.GENERAL_ERROR:
          this.errorMessage = HttpErrorsEnum.GENERAL_ERROR;
          break;
        case HttpErrorsEnum.FAILED_TO_FETCH_DATA:
          this.errorMessage = HttpErrorsEnum.FAILED_TO_FETCH_DATA;
          break;
        case HttpErrorsEnum.TOKEN_EXPIRED:
        case HttpErrorsEnum.INVALID_TOKEN:
          this.errorMessage = HttpErrorsEnum.TOKEN_EXPIRED;
          break;
        default:
          this.errorMessage = HttpErrorsEnum.GENERAL_ERROR;
          break;
      }
    } else
      this.errorMessage = HttpErrorsEnum.GENERAL_ERROR;
  }

  async runAnalysis(run: AnalysisRun): Promise<void> {
    run.assertActive();
    this.logService.logMessage('Analysis Started');
    this.riskService.assessment = new RiskAssessment(RiskLevel.High);
    this.riskService.weightedResult = 0;
    this.riskService.evaluatorsScoreMap = this.evaluatorService.evaluatorsScoreMap;
    const senderEvaluators = await this.evaluatorService.initSenderEvaluators(this.evaluationFlow, run);
    for (const flow of [EvaluationFlowsEnum.SENDER, EvaluationFlowsEnum.IMPERSONATION_RISK]) {
      await this.runCategory(flow, async () => senderEvaluators.filter(evaluator => evaluator.getType() === flow), run);
    }
    run.assertActive();
    this.waitService.hideMessage();
    await Promise.allSettled([
      this.runCategory(EvaluationFlowsEnum.ATTACHMENTS,
        () => this.evaluatorService.initAttachmentEvaluators(this.evaluationFlow, run), run),
      this.runCategory(EvaluationFlowsEnum.LINKS,
        () => this.evaluatorService.initLinksEvaluators(this.evaluationFlow, run), run),
      this.runCategory(EvaluationFlowsEnum.QRCODE,
        () => this.evaluatorService.initQRCodeEvaluators(this.evaluationFlow, run), run)
    ]);
    run.assertActive();
  }

  private async runCategory(flow: EvaluationFlowsEnum, initialize: () => Promise<IEvaluator[]>, run: AnalysisRun): Promise<void> {
    run.assertActive();
    if (run.flows.get(flow).state === 'skipped') return;
    run.flows.get(flow).state = 'running';
    try {
      const evaluators = await initialize();
      run.assertActive();
      for (const evaluator of evaluators) {
        try {
          const result = await this.riskService.performAnalysis(evaluator, run);
          run.assertActive();
          this.evaluatorService.updateTotalScore();
          this.assessment = result;
          this.evaluatorService.assessment = result;
        } catch (error) {
          run.assertActive();
          run.issue(flow, error, `evaluator:${evaluators.indexOf(evaluator)}`);
        }
      }
    } catch (error) {
      run.assertActive();
      run.issue(flow, error, 'initialization');
    } finally { run.complete(flow); }
  }

  toggleMenu() {
    if (this.menuOpen) {
      const menu = document.querySelector('.overlay-menu');
      if (menu) {
        menu.classList.add('closing');
        setTimeout(() => {
          this.menuOpen = false;
          document.body.style.overflow = '';
        }, 500);
      }
    } else {
      this.menuOpen = true;
      document.body.style.overflow = 'hidden';
    }
  }
  
  selectTab(index: TabIndexEnum) {
    this.selectedTabIndex = index;
    this.menuOpen = false;
    document.body.style.overflow = '';
  }
  
  get currentTabTitle(): string {
    let overviewTabTitle = '';
    if(this.selectedTabIndex == 0) {
      overviewTabTitle = this.isAnalyzing ? "shared.analyzing" : (this.runs.current?.incomplete ? "analysis.incomplete" : "overview.phishing_probability");
      return this.translate.instant(overviewTabTitle);
    }
    else
      return this.translate.instant(this.tabTitles[this.selectedTabIndex]);
  }

  enableLocalization(){
    const enableLocalization = this.settingService.getSettingValue(SettingsEnum.ENABLE_LOCALIZATION);
    return enableLocalization == true;
  }

  changeLanguage(lang: string) {
    this.translate.use(lang).subscribe(() => {
      this.currentLang = lang;
      this.currentFlagUrl = LANGUAGES.get(this.currentLang)?.flagUrl ?? LANGUAGES.get("en")?.flagUrl;
      localStorage.setItem(CacheNamesEnum.LANGUAGE, lang);

      if (this.user && this.user.language !== lang) {
        this.user.language = lang;
        localStorage.setItem(CacheNamesEnum.USER, JSON.stringify(this.user));

        this.sharedService.updateUserLanguage(lang).catch((err) => {
          console.error("Failed to update language on server:", err);
        });
      }
      this.cdr.detectChanges();
    });
  }

  initializeLanguageSettings(): void {
    this.user = JSON.parse(localStorage.getItem(CacheNamesEnum.USER)!) as UserDTO;

    const userLang = this.user?.language ;
    if(userLang && userLang !== this.currentLang) {
      this.translate.use(userLang);

      this.currentLang = userLang;
      localStorage.setItem(CacheNamesEnum.LANGUAGE, userLang);
    }
  }
  get isBuildVersion(): boolean {
    return this.env.version === 'BUILD_VERSION';
  }
}
